<kanzi-ui-main-card
  [title]="warehouse()?.name || ('warehouses.detail.title' | translate)"
  description="{{ 'warehouses.detail.description' | translate }}"
>
  <ng-template kTemplate="rightTopContent">
    <div class="flex gap-2">
      <!-- Back button -->
      <p-button
        label="{{ 'common.back' | translate }}"
        icon="pi pi-arrow-left"
        severity="primary"
        [outlined]="true"
        (onClick)="onBack()"
      ></p-button>

      <!-- Edit button -->
      <p-button
        label="{{ 'common.edit' | translate }}"
        icon="pi pi-pencil"
        (onClick)="onEdit()"
        [disabled]="!warehouse()"
    ></p-button>

      <!-- Delete button -->
      <p-button
        icon="pi pi-trash"
        styleClass="p-button-danger"
        (onClick)="onDelete()"
        [disabled]="!warehouse()"
        [loading]="deleteMutation.isPending()"
      ></p-button>
  </div>
      </ng-template>

  <!-- Loading State -->
  @if (warehouseQuery.isPending()) {
  <div class="content-container">
    <p-skeleton width="100%" height="200px"></p-skeleton>
    <p-skeleton width="80%" height="20px" styleClass="mt-3"></p-skeleton>
    <p-skeleton width="60%" height="20px" styleClass="mt-2"></p-skeleton>
          </div>
  }

  <!-- Error State -->
  @if (warehouseQuery.isError()) {
  <div class="content-container text-center py-5">
    <i class="pi pi-exclamation-circle text-6xl text-red-500 mb-3"></i>
    <p class="text-xl font-semibold text-color-secondary">
      {{ 'warehouses.detail.error' | translate }}
            </p>
    <p class="text-color-secondary">
      {{ warehouseQuery.error().message }}
            </p>
    <p-button
      label="{{ 'common.back' | translate }}"
      icon="pi pi-arrow-left"
      severity="primary"
      [outlined]="true"
      (onClick)="onBack()"
      styleClass="mt-3"
    ></p-button>
          </div>
        }

  <!-- Main Content -->
  @if (warehouse(); as wh) {
  <div class="content-container">
    <!-- Header Card -->
    <p-card styleClass="mb-4">
      <div class="flex align-items-start justify-content-between">
        <div class="flex align-items-center gap-3">
          <!-- Warehouse icon -->
          <div
            class="bg-primary-gradient flex align-items-center justify-content-center"
            style="width: 64px; height: 64px; border-radius: 12px"
          >
            <i class="pi pi-building text-white text-3xl"></i>
          </div>
          <div>
            <!-- Warehouse name -->
            <h2 class="mt-0 mb-1">{{ wh.name }}</h2>
            <!-- External ID if available -->
            @if (wh.external_id) {
        <p class="text-color-secondary m-0">
              <i class="pi pi-tag mr-1"></i>
              {{ wh.external_id }}
        </p>
      }
  </div>
        </div>

        <!-- Warehouse type tag -->
        <p-tag
          value="{{ wh.type.name || ('warehouses.noType' | translate) }}"
          severity="success"
        ></p-tag>
      </div>

      <!-- Description section -->
      @if (wh.description) {
      <p-divider></p-divider>
      <p class="m-0 text-color-secondary">{{ wh.description }}</p>
  }
    </p-card>

    <!-- General Information -->
    <p-card styleClass="mb-4">
      <ng-template pTemplate="title">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-info-circle text-primary"></i>
          {{ 'warehouses.detail.generalInfo' | translate }}
        </div>
      </ng-template>

      <div class="grid">
        <!-- Type -->
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'warehouses.detail.type' | translate }}
            </span>
            <p class="mt-1 mb-0">
              @if (wh.type) {
              {{ wh.type.name }}
              } @else {
              <span class="text-color-secondary">N/A</span>
              }
            </p>
          </div>
        </div>

        <!-- Customer -->
        @if (wh.customer) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'warehouses.detail.customer' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.customer }}</p>
          </div>
        </div>
        }

        <!-- Created by -->
        @if (wh.created_by) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'warehouses.detail.createdBy' | translate }}
            </span>
            <p class="mt-1 mb-0 flex align-items-center gap-2">
              <i class="pi pi-user"></i>
              {{ wh.created_by.username }}
            </p>
          </div>
        </div>
        }

        <!-- Creation date -->
        @if (wh.created) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'warehouses.detail.createdAt' | translate }}
            </span>
            <p class="mt-1 mb-0 flex align-items-center gap-2">
              <i class="pi pi-calendar"></i>
              {{ wh.created }}
            </p>
          </div>
        </div>
        }

        <!-- Last modification -->
        @if (wh.modified) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1"></span>
              {{ 'warehouses.detail.modifiedAt' | translate }}
            <p class="mt-1 mb-0 flex align-items-center gap-2">
              <i class="pi pi-calendar"></i>
              {{ wh.modified }}
            </p>
          </div>
        </div>
        }
      </div>
    </p-card>

    <!-- Geographic Information -->
    @if (wh.country || wh.state || wh.city || wh.address || wh.postal_code) {
    <p-card styleClass="mb-4">
      <ng-template pTemplate="title">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-map-marker text-primary"></i>
          {{ 'location.geographic_info' | translate }}
        </div>
      </ng-template>

      <div class="grid">
        <!-- Country -->
        @if (wh.country) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.country' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.country.name }}</p>
          </div>
        </div>
        }

        <!-- State -->
        @if (wh.state) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.state' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.state.name }}</p>
          </div>
        </div>
        }

        <!-- City -->
        @if (wh.city) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.city' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.city.name }}</p>
          </div>
        </div>
        }

        <!-- Postal Code -->
        @if (wh.postal_code) {
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.postal_code' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.postal_code }}</p>
          </div>
        </div>
        }

        <!-- Address -->
        @if (wh.address) {
        <div class="col-12">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.address' | translate }}
            </span>
            <p class="mt-1 mb-0">{{ wh.address }}</p>
          </div>
        </div>
        }
      </div>
    </p-card>
    }

    <!-- Contact Information -->
    <p-card styleClass="mb-4">
      <ng-template pTemplate="title">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-phone text-primary"></i>
          {{ 'location.contact_info' | translate }}
        </div>
      </ng-template>

      <div class="grid">
        <!-- Phone -->
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.phone' | translate }}
            </span>
            <p class="mt-1 mb-0 flex align-items-center gap-2">
              <i class="pi pi-phone"></i>
              {{ wh.phone || 'N/A' }}
            </p>
          </div>
        </div>

        <!-- Mobile -->
        <div class="col-12 md:col-6">
          <div class="mb-3">
            <span class="font-semibold text-color-secondary text-sm block mb-1">
              {{ 'location.mobile' | translate }}
            </span>
            <p class="mt-1 mb-0 flex align-items-center gap-2">
              <i class="pi pi-mobile"></i>
              {{ wh.mobile || 'N/A' }}
            </p>
          </div>
        </div>
      </div>
    </p-card>

    <!-- Operating Hours -->
    <p-card styleClass="mb-4">
      <ng-template pTemplate="title">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-clock text-primary"></i>
          {{ 'location.schedule_info' | translate }}
        </div>
      </ng-template>

      <!-- Schedule table if operating hours exist -->
      @if (wh.operating_hours && Object.keys(wh.operating_hours).length > 0) {
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-bottom-1 surface-border">
              <th scope="col" class="text-left p-3 font-semibold text-color-secondary">
                {{ 'location.day' | translate }}
              </th>
              <th scope="col" class="text-left p-3 font-semibold text-color-secondary">
                {{ 'location.opening_time' | translate }}
              </th>
              <th scope="col" class="text-left p-3 font-semibold text-color-secondary">
                {{ 'location.closing_time' | translate }}
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Loop through schedule array -->
            @for (schedule of getScheduleArray(wh.operating_hours); track schedule.dayOfWeek) {
            <tr class="border-bottom-1 surface-border">
              <td class="p-3">
                {{ getDayName(schedule.dayOfWeek) | translate }}
              </td>
              <td class="p-3">
                @if (!schedule.hours) {
                <span class="text-color-secondary">{{ 'location.closed' | translate }}</span>
                } @else {
                {{ schedule.hours.open_time }}
                }
              </td>
              <td class="p-3">
                @if (!schedule.hours) {
                <span class="text-color-secondary">{{ 'location.closed' | translate }}</span>
                } @else {
                {{ schedule.hours.close_time }}
                }
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>
      } @else {
      <!-- No schedule available message -->
      <div class="text-center py-4">
        <i class="pi pi-calendar-times text-4xl text-color-secondary mb-2"></i>
        <p class="text-color-secondary m-0">
          {{ 'location.no_schedule' | translate }}
        </p>
      </div>
      }
    </p-card>
  </div>
  }
</kanzi-ui-main-card>

<!-- Confirm Dialog -->
<p-confirmDialog></p-confirmDialog>
